import type { ApiEnvelope } from "./types";
import { supabase } from "./supabase";

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/+$/, "");
}

const API_BASE_URL = normalizeBaseUrl(import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3000");

const DEFAULT_TIMEOUT_MS = 15_000; // 15 seconds

// LocalStorage key for selected center (multi-center support)
const SELECTED_CENTER_KEY = "cradleos_selected_center";

/** Get the currently selected center ID from localStorage, or null during loading/sign-out. */
export function getSelectedCenterId(): number | null {
  const val = localStorage.getItem(SELECTED_CENTER_KEY);
  if (!val) return null;
  const id = Number(val);
  return Number.isFinite(id) ? id : null;
}

/** Set the selected center ID in localStorage. Pass null to clear (used during sign-out). */
export function setSelectedCenterId(id: number | null): void {
  if (id === null) {
    localStorage.removeItem(SELECTED_CENTER_KEY);
  } else {
    localStorage.setItem(SELECTED_CENTER_KEY, String(id));
  }
}

/** Clear the selected center ID (used on sign out). */
export function clearSelectedCenterId(): void {
  localStorage.removeItem(SELECTED_CENTER_KEY);
}

/**
 * Converts a relative API path (like /rails/active_storage/...) to a full URL.
 * Use this for image/asset URLs returned by the API.
 */
export function getApiAssetUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  // If already a full URL, return as-is
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  return `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

// Get current access token from Supabase session
async function getSupabaseToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export class ApiError extends Error {
  status: number;
  errors: string[];
  unauthorized: boolean;
  errorCategory?: string;

  constructor(message: string, opts: { status: number; errors: string[]; unauthorized?: boolean; errorCategory?: string }) {
    super(message);
    this.name = "ApiError";
    this.status = opts.status;
    this.errors = opts.errors;
    this.unauthorized = Boolean(opts.unauthorized);
    this.errorCategory = opts.errorCategory;
    // Maintains proper stack trace in V8 environments
    if ((Error as any).captureStackTrace) {
      (Error as any).captureStackTrace(this, ApiError);
    }
  }
}

export type ApiFetchResult<T> = {
  data: T;
  authorizationHeader: string | null;
};

export async function apiFetch<TData>(
  path: string,
  opts?: {
    method?: string;
    body?: unknown;
    token?: string | null;
    headers?: Record<string, string>;
    signal?: AbortSignal;
    timeoutMs?: number;
  }
): Promise<ApiFetchResult<TData>> {
  const url = `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  // Use explicitly provided token, or fall back to Supabase session token
  const token = opts?.token !== undefined ? opts.token : await getSupabaseToken();
  const isFormData = typeof FormData !== "undefined" && opts?.body instanceof FormData;
  const hasBody = typeof opts?.body !== "undefined" && opts?.body !== null;

  // Build abort signal: combine caller signal with timeout signal
  const timeoutMs = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const signal = opts?.signal
    ? AbortSignal.any([opts.signal, timeoutSignal])
    : timeoutSignal;

  let res: Response;
  try {
    // Include X-Center-Id header for multi-center scoping
    const centerId = getSelectedCenterId();

    res = await fetch(url, {
      method: opts?.method ?? "GET",
      headers: {
        Accept: "application/json",
        ...(hasBody && !isFormData ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(centerId ? { "X-Center-Id": String(centerId) } : {}),
        ...(opts?.headers ?? {})
      },
      body: hasBody ? (isFormData ? (opts!.body as FormData) : JSON.stringify(opts!.body)) : undefined,
      signal
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "TimeoutError") {
      throw new ApiError("Request timed out", {
        status: 0,
        errors: ["The server took too long to respond. Please try again."],
      });
    }
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError("Request aborted", {
        status: 0,
        errors: ["The request was cancelled."],
      });
    }
    // Network error (DNS failure, server unreachable, etc.)
    throw new ApiError("Network error", {
      status: 0,
      errors: ["Unable to connect to the server. Please check your connection and try again."],
    });
  }

  const authorizationHeader = res.headers.get("Authorization");
  const contentType = res.headers.get("content-type") ?? "";
  const rawText = await res.text();

  let json: unknown = null;
  if (contentType.includes("application/json") && rawText) {
    try {
      json = JSON.parse(rawText);
    } catch {
      json = null;
    }
  }

  // API uses a consistent envelope, but still guard for non-json / unexpected errors.
  const envelope = json as ApiEnvelope<TData> | null;

  if (!res.ok) {
    const unauthorized = res.status === 401;
    // Note: We no longer clear tokens here - Supabase manages session state
    // The AuthProvider will handle signing out if needed

    const envelopeErrors =
      envelope && envelope.status === "error"
        ? (envelope.data.errors ?? []).filter(Boolean)
        : [];

    const fallback =
      rawText && !contentType.includes("application/json")
        ? [`Request failed (HTTP ${res.status}): ${rawText.slice(0, 300)}`]
        : [`Request failed (HTTP ${res.status})`];

    const errors = envelopeErrors.length ? envelopeErrors : fallback;

    const errorCategory = envelope?.status === "error"
      ? (envelope.data as Record<string, unknown>)?.error_category as string | undefined
      : undefined;

    const apiError = new ApiError(`HTTP ${res.status}`, { status: res.status, errors, unauthorized, errorCategory });

    throw apiError;
  }

  // e.g. DELETE returning 204
  if (!rawText) {
    return { data: {} as TData, authorizationHeader };
  }

  if (!envelope || envelope.status !== "success") {
    throw new ApiError("Unexpected API response", { status: res.status, errors: ["Unexpected API response"] });
  }

  return { data: envelope.data, authorizationHeader };
}


